import { Component, Input } from '@angular/core';
import { FormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { sanitizeToDate } from 'src/app/shared/helpers/formatDates';
import { InventoryStore } from 'src/app/stores/component/inventory-store';
import { ICreateInventoryDeviceServiceRequest, IInventoryDeviceDetails, IInventoryDeviceService, IUpdateInventoryDeviceServiceRequest } from '../../models/inventory-device';

/**
 * Services (executed maintenance) of an inventory device. Every change is sent through the parent's
 * InventoryStore, which replaces the device with the backend response (recalculated next service date).
 */
@Component({
    selector: 'app-inventory-detail-services',
    templateUrl: './inventory-detail-services.component.html',
    styleUrl: './inventory-detail-services.component.scss',
    standalone: false
})
export class InventoryDetailServicesComponent {
  @Input() device: IInventoryDeviceDetails | null;

  editingService: IInventoryDeviceService | null = null;
  serviceDialogOpened = false;
  deletionDialogOpened = false;

  public serviceForm: UntypedFormGroup;

  constructor(
    private fb: FormBuilder,
    private inventoryStore: InventoryStore
  ) {
    this.serviceForm = this.fb.group({
      executionDate: new UntypedFormControl(null, {
        validators: Validators.required
      }),
      remark: new UntypedFormControl(null)
    });
  }

  get services(): IInventoryDeviceService[] {
    return this.device?.services ?? [];
  }

  formatDate(date: string): string {
    return date ? sanitizeToDate(date) : '-';
  }

  public openServiceDialog(service?: IInventoryDeviceService): void {
    this.editingService = service || null;

    if (service) {
      this.serviceForm.patchValue({
        executionDate: service.executionDate ? new Date(service.executionDate) : null,
        remark: service.remark
      });
    } else {
      this.serviceForm.reset();

      // Prefill with the planned next service date, so recording the planned service is a single click.
      // The form is marked dirty because the submit button requires a change.
      if (this.device?.nextService) {
        this.serviceForm.patchValue({ executionDate: new Date(this.device.nextService) });
        this.serviceForm.markAsDirty();
      }
    }

    this.serviceDialogOpened = true;
  }

  public closeServiceDialog(): void {
    this.serviceDialogOpened = false;
    this.editingService = null;
    this.serviceForm.reset();
  }

  submitService() {
    if (this.editingService) {
      const updateRequest: IUpdateInventoryDeviceServiceRequest = {
        id: this.editingService.id,
        executionDate: this.serviceForm.value.executionDate,
        remark: this.serviceForm.value.remark
      };
      this.inventoryStore.updateService(updateRequest);
    } else {
      const request: ICreateInventoryDeviceServiceRequest = {
        inventoryDeviceId: this.device.id,
        executionDate: this.serviceForm.value.executionDate,
        remark: this.serviceForm.value.remark
      };
      this.inventoryStore.addService(request);
    }

    this.closeServiceDialog();
  }

  // delete confirmation dialog
  public deletionDialogOpen(): void {
    this.deletionDialogOpened = true;
  }

  public deletionDialogClose(status: string): void {
    this.deletionDialogOpened = false;

    if (status == 'delete' && this.editingService) {
      this.inventoryStore.removeService(this.editingService.id);
      this.closeServiceDialog();
    }
  }
}